import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { executeTriggersForStatus, WorkflowResult } from '../../services/workflowTriggers';
import './BillOfSaleForm.css';

interface BillOfSaleFormProps {
  deal: any;
  onComplete?: () => void;
  /* Why the seller is being told again. Without it a resend is refused. */
  repeatReason?: string
}

const dealPrice = (deal: any): number | undefined =>
  /* Agreed purchase price first (Winnie, 10 Oct). */
  deal.valuation_amount || deal.autograb_market_value;

/**
 * The $500 holding deposit was removed in v2.0-NL.2.0 ("NO DEPOSIT", Critical
 * Business Rules). It used to gate PPSR lodgement — you could not elect
 * lodgement without electing a deposit — so removing it also frees lodgement to
 * be what the spec says it is: an unconditional yes/no for the sales team.
 */
export const BillOfSaleForm: React.FC<BillOfSaleFormProps> = ({ deal, onComplete, repeatReason }) => {
  const [lodgementElected, setLodgementElected] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [recordNote, setRecordNote] = useState<string | null>(null)
  // What the Under Offer triggers actually did. The success screen used to
  // assert an SMS had gone out without ever looking.
  const [triggerOutcomes, setTriggerOutcomes] = useState<WorkflowResult[]>([]);

  const generateBOS = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      // Create Vehicle Purchase Agreement document record
      const { data, error: insertError } = await supabase
        .from('bill_of_sale_documents')
        .insert([{
          deal_id: deal.id,
          deposit_elected: false,
          lodgement_elected: lodgementElected,
          elections_locked_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (insertError) throw insertError;

      // Update deal with elections + BOS sent timestamp
      const { error: updateError } = await supabase
        .from('deals')
        .update({
          bill_of_sale_document_id: data.id,
          deposit_elected: false,
          lodgement_elected: lodgementElected,
          elections_locked_at: new Date().toISOString(),
          deal_status: 'under_offer'
        })
        .eq('id', deal.id);

      if (updateError) throw updateError;

      // Fire the Under Offer triggers: seller Bill-of-Sale email, BOS SMS, and
      // inspection booking.
      //
      // This used to be fire-and-forget with `.catch(console.error)`, and the
      // success screen then said "SMS sent to <number>" unconditionally — even
      // when the deal had no seller phone at all, when the number failed
      // normalisation, when the seller had opted out, or when the provider
      // rejected it. The seller was never told their Vehicle Purchase Agreement was ready
      // and nobody in the office knew. The results are now waited for and
      // reported.
      const triggerResults = await executeTriggersForStatus(deal.id, 'under_offer', repeatReason).catch(
        (e): WorkflowResult[] => [
          {
            success: false,
            message: e instanceof Error ? e.message : 'Under Offer triggers failed',
          },
        ]
      );
      setTriggerOutcomes(triggerResults || []);

      /*
       * Only now is it true that the seller was told.
       *
       * This stamp used to be written above, before a single message had even
       * been attempted, and nothing ever took it back. A deal whose email and
       * text were both refused still read "Sent to the seller" on the card and
       * in the record - to us, to a seller chasing us, and to anyone reading it
       * back in six years. It happened to two deals during a beta test.
       *
       * If nothing got through, nothing is stamped, and the card says so.
       */
      const told = (triggerResults || []).some((r) => r.success)
      if (told) {
        const { error: stampError } = await supabase
          .from('deals')
          .update({ bill_of_sale_sent_at: new Date().toISOString() })
          .eq('id', deal.id)
        /*
         * The seller really was contacted - that is what told means. If the stamp did not
         * save, the deal will keep looking as though the Vehicle Purchase Agreement was never sent and
         * will happily offer to send it again. Say so on the screen rather than leaving
         * the record and the world quietly disagreeing.
         */
        if (stampError) {
          setRecordNote('The seller was contacted, but we could not record that on the deal (' + stampError.message + '). Until that is fixed the deal will still show the Vehicle Purchase Agreement as not sent, and may offer to send it a second time.')
        }
      }

      setSuccess(true);
      setTimeout(() => {
        if (onComplete) onComplete();
      }, 2000);

    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMsg);
      console.error('BOS generation error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    const failures = triggerOutcomes.filter((r) => !r.success);
    return (
      <div className="bos-success">
        <div className="success-check">{failures.length ? '!' : '✓'}</div>
        <h3>Vehicle Purchase Agreement Generated</h3>
        {recordNote && <p className="bos-record-note">{recordNote}</p>}
        {failures.length === 0 ? (
          <>
            <p>Sent to {deal.seller_phone || 'the seller'}</p>
            <p>Seller has 14 days to sign</p>
          </>
        ) : (
          <div className="bos-trigger-failures">
            <p><strong>The Vehicle Purchase Agreement is saved, but the seller was not told:</strong></p>
            <ul>
              {failures.map((f, i) => (
                <li key={i}>{f.message}</li>
              ))}
            </ul>
            <p>Contact the seller directly, or fix the problem above and re-send from the deal.</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bos-form-container">
      <div className="bos-form-card">
        <h2>Generate Vehicle Purchase Agreement</h2>

        <div className="deal-summary">
          <h3>Vehicle Summary</h3>
          <div className="summary-grid">
            <div className="summary-item">
              <span className="label">Vehicle:</span>
              <span className="value">
                {deal.autograb_year ?? deal.vehicle_year} {deal.autograb_make ?? deal.vehicle_make} {deal.autograb_model ?? deal.vehicle_model}
              </span>
            </div>
            <div className="summary-item">
              <span className="label">VIN:</span>
              <span className="value">{deal.autograb_vin ?? deal.vehicle_vin}</span>
            </div>
            <div className="summary-item">
              <span className="label">Seller:</span>
              <span className="value">{deal.seller_name}</span>
            </div>
            <div className="summary-item">
              <span className="label">Price:</span>
              <span className="value">${dealPrice(deal)?.toLocaleString('en-AU')}</span>
            </div>
            <div className="summary-item">
              <span className="label">Phone:</span>
              <span className="value">{deal.seller_phone}</span>
            </div>
            <div className="summary-item">
              <span className="label">Email:</span>
              <span className="value">{deal.seller_email}</span>
            </div>
          </div>
        </div>

        <div className="elections-section">
          <h3>Seller Elections</h3>
          <p className="elections-info">
            This selection is locked once the Vehicle Purchase Agreement is sent, and cannot be changed without
            an admin override. There is no holding deposit — it was removed in v2.0-NL.2.0.
          </p>

          <div className="election-option">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={lodgementElected}
                onChange={(e) => setLodgementElected(e.target.checked)}
              />
              <span className="checkbox-text">
                <strong>PPSR Lodgement</strong>
                <br />
                <span className="checkbox-desc">
                  Register a security interest over the vehicle until settlement. Optional, and
                  elected by the sales team.
                </span>
              </span>
            </label>
          </div>
        </div>

        <div className="payment-terms">
          <h3>Payment Terms</h3>
          <div className="terms-box">
            <p><strong>Purchase Price:</strong> ${dealPrice(deal)?.toLocaleString('en-AU')}</p>
            <p><strong>Payment Method:</strong> Online Bank Transfer (same day or next business day)</p>
            <p><strong>Settlement Terms:</strong> Within 3 business days of signed contract</p>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}

        <div className="bos-actions">
          <button
            className="btn-generate-bos"
            onClick={generateBOS}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Generating...' : 'Generate & Send BOS'}
          </button>
          <p className="action-note">
            This will generate the Vehicle Purchase Agreement, lock the elections, and send an SMS to the seller.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BillOfSaleForm;
